import type { BookingFlowShellConfig } from "../common/components/BookingFlowShell/BookingFlowShell";
import { ServiceTypeSelectorOption } from "../../../types/order";
import type { UseCase } from "../../../util/useCases";
import { DEFAULT_EVENT_FLOW_USE_CASE } from "./eventFlowUseCase";

/**
 * Venue reservation, on the shared booking shell (ADR-0026, phase 1 step 5).
 *
 * This flow's copy of the page declared its own `BookingCreationStep`, which
 * was numerically identical to the room one and differed only in calling step
 * 5 `success` instead of `payment`. Nothing outside this file imported it, so
 * the shared enum replaces it with no change in step numbering.
 *
 * What is NOT obvious and is deliberate:
 *
 * - For a venue, `requestOrderUseCase` is `undefined`, not the use case.
 *   `createVenueReservationFlow` defaults it to the venue, so the two ARE
 *   equivalent there — it is left absent to match today exactly rather than
 *   to read better. A sports hall (same flow, PO 2026-10-18) passes its own,
 *   or its request would be filed as a venue.
 * - `updateServiceOn: "mount"` — this flow writes the selected service on
 *   mount, where room does it at the summary.
 * - `appliesVoucher: false` — this flow passes no voucher when updating the
 *   reservation.
 */
export const configFor = (useCase: UseCase): BookingFlowShellConfig => {
  // The flow's own words: the venue's as before, a hall's under `hall`.
  const own = useCase !== DEFAULT_EVENT_FLOW_USE_CASE;
  return {
    directBookingKey: useCase,
    serviceType: useCase as ServiceTypeSelectorOption,
    updateServiceOn: "mount",
    stepWrapperId: "venueProcessStepWrapper",
    paidReservationUseCase: useCase,
    requestOrderUseCase: own ? useCase : undefined,
    appliesVoucher: false,
    passesUpdateHandlerInRequestFlow: false,
    headerKeys: {
      overview: own
        ? `book_location.venue_reservation_overview.header_text_${useCase}`
        : "book_location.venue_reservation_overview.header_text",
      personalData: "book_location.venue_reservation_overview.personal_data",
    },
    objectsFoundKey: `${useCase}.${useCase}_found`,
    searchErrorMessageKey: `book_location.error.fetch_${useCase}s`,
  };
};
